import React, { useEffect, useState } from "react";
import Layout from "src/components/app/layout/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import TestDetailsInCart from "src/components/app/lab-tests/TestDetailsInCart";
import { Briefcase, Home, MapPin } from "lucide-react";
import BillingView from "src/components/app/lab-tests/BillingView";
import Button from "src/components/app/Button";
import Card from "src/components/app/Card/Card";
import AddAddressBottomSheet from "src/components/app/bottom-sheet/AddAddressBottomSheet";
import AddPatientBottomSheet from "src/components/app/bottom-sheet/AddPatientBottomSheet";
import { GetLabTestCart } from "src/types/labTests.types";
import { getAddressListRequest } from "src/redux/slices/locationSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { AddressListResponse } from "src/types/location.types";
import { useLabTestCart } from "src/helpers/common";
import DiscountSuccessModal from "src/components/app/modals/common/DiscountSuccessModal";
import { resetCoupanDiscount } from "src/redux/slices/offersSlice";
import WalletUsageSection from "src/components/app/WalletUsageSection";
import supportPrograms from "src/data/app/programs";

const LabTestCheckoutScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isPatientOpen, setIsPatientOpen] = useState(false);
  const [showDiscountSuccess, setShowDiscountSuccess] = useState(false);
  const location = useLocation();
  const cartDetails: GetLabTestCart | null = useAppSelector(
    (state) => state.labTest.cartState.data,
  );
  const dispatch = useAppDispatch();
  const addressList = useAppSelector((state) => state.location.getAddressList);
  const addresses = addressList?.data || [];
  const [currentAddress, setCurrentAddress] =
    useState<AddressListResponse | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const { fetchLabTestCart } = useLabTestCart();
  const discountData = useAppSelector(
    (state) => state.offers.CoupanDiscount.data,
  );
  const [orderTotal, setOrderTotal] = useState<number | null>(null);
  const [homeCollectionCharge, setHomeCollectionCharge] = useState<
    number | null
  >(null);
  const [serviceCharge, setServiceCharge] = useState<number | null>(null);
  const [finalPayableAmount, setFinalPayableAmount] = useState<number | null>(
    null,
  );
  const [payableAmount, setPayableAmount] = useState<number | null>(null);
  const [useWalletMoney, setUseWalletMoney] = useState<boolean>(false);
  const [walletUsage, setWalletUsage] = useState<number>(0);

  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const isWalletValid = config?.config?.enableWallet;

  const icons = {
    Home: <Home className="w-5 h-5" />,
    Work: <Briefcase className="w-5 h-5" />,
    Other: <MapPin className="w-5 h-5" />,
  };

  useEffect(() => {
    if (location.state?.isRefetchCartDetails) {
      fetchLabTestCart();
      // Clear the state after using it
      navigate(location.pathname, { replace: true, state: null });
    }

    // Check if we should show discount success popup
    if (location.state?.showDiscountSuccess && discountData) {
      setShowDiscountSuccess(true);
      // Clear the state after using it
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [
    location.state?.isRefetchCartDetails,
    location.state?.showDiscountSuccess,
    fetchLabTestCart,
    navigate,
    location.pathname,
    discountData,
  ]);

  useEffect(() => {
    getAddressList();
    extractBillingDetails();
    // Reset any applied discount when entering checkout screen
    if (!location.state?.showDiscountSuccess) {
      dispatch(resetCoupanDiscount());
    }
  }, []);

  const extractBillingDetails = () => {
    if (cartDetails?.order_detail) {
      setOrderTotal(Number(cartDetails?.order_detail?.order_total));
      setHomeCollectionCharge(
        cartDetails?.home_collection_charge?.payable_ammount ||
          cartDetails?.home_collection_charge?.ammount ||
          0,
      );
      setServiceCharge(cartDetails?.order_detail?.service_charge);
      setFinalPayableAmount(cartDetails?.order_detail?.final_payable_amount);
      setPayableAmount(Number(cartDetails?.order_detail?.payable_amount));
    }
  };

  useEffect(() => {
    if (cartDetails?.address_data_obj) {
      setCurrentAddress(cartDetails?.address_data_obj as AddressListResponse);
      setAddress(cartDetails?.address_data_obj?.address);
    }
  }, [addressList, addressList?.data, cartDetails]);

  // Update billing details when cart changes (e.g., after deleting items)
  useEffect(() => {
    if (cartDetails?.order_detail) {
      extractBillingDetails();
    }
  }, [cartDetails?.order_detail]);

  const getAddressList = () => {
    new Promise((resolve, reject) => {
      dispatch(getAddressListRequest({ resolve, reject, payload: {} }));
    });
  };

  const handleDiscountSuccessClose = () => {
    setShowDiscountSuccess(false);
  };

  if (!cartDetails) {
    return <div>No cart details found</div>;
  }

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
        <Header
          title="Lab Test Cart"
          titlePosition="left"
          onBackClick={() => {
            navigate(`/app/${supportProgramKey}/book-lab-test`);
          }}
        />
      </div>

      {/* Page Content */}
      <div className="pt-16 pb-40 px-4 space-y-4">
        {/* Test Details */}
        <div>
          <h2 className="text-primary text-md font-semibold mb-2">
            Cart Details
          </h2>
          <Card stackTitleAndDescription={true}>
            <TestDetailsInCart testListInCart={cartDetails?.tests_list} />
          </Card>
        </div>
        {/* Offers & Promotions */}
        {/* <div>
          <h2 className="text-md font-semibold mb-2">Offers & Promotions</h2>
          <Card
            stackTitleAndDescription={true}
            onClick={() => {
              navigate(`/app/${supportProgramKey}/book-lab-test/apply-coupon`);
            }}
          >
            <div className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center space-x-2">
                <div className="bg-green-100 text-green-600 p-1 rounded-full text-sm">
                  <BadgePercent className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">
                  {discountData ? "Applied Coupon" : "Apply Coupon"}
                </span>
              </div>
              <span className="text-gray-400 text-lg">&gt;</span>
            </div>
            {discountData && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {discountData.discount_code?.coupon_name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {discountData.discount_code?.label}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">
                      -₹{discountData.discount_amount}
                    </p>
                    <p className="text-xs text-gray-500">Applied</p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div> */}

        {/* Wallet Money Section - Using centralized component */}
        {isWalletValid && (
          <WalletUsageSection
            bookingAmount={Number(finalPayableAmount)}
            discountAmount={Number(discountData?.discount_amount || 0)}
            onUsedWalletMoney={(usedWalletMoney: number) => {
              if (usedWalletMoney > 0) {
                setUseWalletMoney(true);
                setWalletUsage(Number(usedWalletMoney));
              } else {
                setUseWalletMoney(false);
                setWalletUsage(0);
              }
            }}
          />
        )}

        {/* Billing View */}
        <div>
          <h2 className="text-primary text-md font-semibold mb-2">Billing</h2>
          <Card stackTitleAndDescription={true}>
            <BillingView
              billingTitle="Item Total"
              discountTitle="Discount on Item(s)"
              orderTotal={String(orderTotal || 0)}
              homeCollectionCharge={homeCollectionCharge || 0}
              serviceCharge={Number(serviceCharge || 0)}
              finalPayableAmount={
                //cartDetails?.order_detail?.final_payable_amount
                Number(finalPayableAmount) -
                Number(discountData?.discount_amount || 0) -
                (useWalletMoney ? walletUsage : 0)
              }
              discount={Number(orderTotal) - Number(payableAmount)}
              couponDiscount={Number(discountData?.discount_amount || 0)}
              usedWalletMoney={useWalletMoney ? walletUsage : 0}
            />
          </Card>
        </div>
      </div>

      {/* Bottom Fixed Sheet */}
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-[0_-2px_10px_rgba(0,0,0,0.1)] px-4 py-5 max-w-lg mx-auto w-full">
        {/* Address Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {address ? (
              <>
                {icons[currentAddress?.address_type as keyof typeof icons]}
                <span className="text-sm text-gray-700 truncate overflow-hidden whitespace-nowrap flex-1">
                  {address}
                </span>
              </>
            ) : (
              <>
                <span className="text-gray-500">📍</span>
                <span className="text-sm text-gray-700">Select Address</span>
              </>
            )}
          </div>
          <p
            className="text-sm text-green-600 font-medium"
            onClick={() => {
              setIsAddressOpen(true);
            }}
          >
            {address ? "Change" : "Add Address"}
          </p>
        </div>

        {/* Select Patient Button */}
        <Button
          rounded="full"
          fullWidth
          disabled={!address}
          onClick={() => {
            setIsPatientOpen(true);
          }}
        >
          Select Patient
        </Button>
      </div>

      {isAddressOpen && (
        <AddAddressBottomSheet
          showBottomSheet={isAddressOpen}
          setShowBottomSheet={setIsAddressOpen}
          addressList={addresses || []}
          onAddressSelect={(address: AddressListResponse) => {
            setCurrentAddress(address);
            setAddress(address.address);
            setIsAddressOpen(false);
          }}
        />
      )}
      {isPatientOpen && (
        <AddPatientBottomSheet
          showBottomSheet={isPatientOpen}
          setShowBottomSheet={setIsPatientOpen}
          address={currentAddress || ({} as AddressListResponse)}
          setPatientModal={setIsPatientOpen}
        />
      )}

      {/* Discount Success Modal */}
      <DiscountSuccessModal
        show={showDiscountSuccess}
        onClose={handleDiscountSuccessClose}
        discountName={discountData?.discount_code?.coupon_name || ""}
        discountAmount={discountData?.discount_amount || 0}
      />
    </Layout>
  );
};

export default LabTestCheckoutScreen;
